import axios, { AxiosResponse } from 'axios'

import { env } from '@js/utils/env'
import gql from '@js/api/utils/gql'
import getStoreName from '@js/api/utils/getStoreName'

type StockStatus = 'IN_STOCK' | 'OUT_OF_STOCK'

interface GetProductStockStatusResponse {
  data: {
    products: {
      total_count: number
      items: Array<{
        sku: string
        stock_status: StockStatus
      }>
    }
  }
  errors?: [{ message: string }]
}

const productStockStatusQuery = gql`
  query Products($sku: String!) {
    products(filter: { sku: { eq: $sku } }) {
      total_count
      items {
        sku
        stock_status
      }
    }
  }
`

/**
 * Fetches the stock status for a product by SKU from Magento via GraphQL.
 *
 * Returns the stock_status string ('IN_STOCK' or 'OUT_OF_STOCK'), or null if
 * the product is not found or an error occurs. currently, 'OUT_OF_STOCK' won't be returned * but instead an empty items array - this is accounted for.
 *
 * @throws {string}
 */
export const getProductStockStatus = async (sku: string): Promise<StockStatus | null> => {
  const { data }: AxiosResponse<GetProductStockStatusResponse> = await axios({
    method: 'POST',
    url: env.magento.GRAPHQL_URL,
    headers: {
      'Content-Type': 'application/json',
      Store: getStoreName(),
    },
    data: {
      query: productStockStatusQuery,
      variables: { sku },
    },
  })

  if (data.errors) {
    throw data.errors[0].message
  }

  const { items, total_count } = data.data.products

  if (total_count === 0 || items.length === 0) {
    // product not found — either no such SKU exists, or it's hidden from catalog queries by Magento's out-of-stock visibility settings.
    // note: products must be set to "Catalog, Search" visibility for GraphQL to return them.
    return null
  }

  return items[0].stock_status
}
